import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Contract } from '@shared/definitions/contract';
import { ContractTermination } from '@shared/definitions/contract-termination';
import { Payment } from '@shared/definitions/payment';
import { PlannedPayment } from '@shared/definitions/payment-planned';
import { ContractsService } from '@core/services/data/contracts.service';

/**
 * Phase 4 — signal-based store for the contract list + currently-viewed
 * contract. Mirrors PropertyStore / WorkspaceStore / AuthStore.
 *
 * Write actions delegate to ContractsService via a lazy Injector lookup
 * (same pattern as LoanStore / TenantStore) — avoids the circular DI that
 * would happen if the store injected the service directly while the
 * service injects the store for the read-path bridge.
 */
@Injectable({ providedIn: 'root' })
export class ContractStore {
  private readonly _list = signal<readonly Contract[]>([]);
  private readonly _currentId = signal<string | null>(null);
  private readonly _loading = signal(false);

  private readonly injector = inject(Injector);
  private _service: ContractsService | null = null;
  private get service(): ContractsService {
    return (this._service ??= this.injector.get(ContractsService));
  }

  readonly list = this._list.asReadonly();
  readonly currentId = this._currentId.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly count = computed(() => this._list().length);

  /**
   * O(1) id→entity index, rebuilt (via computed) only when the list changes.
   * Keeps `getById` off an O(n) linear scan on hot per-card lookup paths.
   */
  private readonly _byId = computed(() => {
    const index = new Map<string, Contract>();
    for (const c of this._list()) index.set(c.contractUID, c);
    return index;
  });

  readonly current = computed(() => {
    const id = this._currentId();
    if (!id) return null;
    return this._byId().get(id) ?? null;
  });

  readonly byPropertyId = (propertyUID: string) =>
    computed(() => this._list().filter(c => c.propertyUID === propertyUID));

  /** Synchronous lookup. */
  getById(contractUID: string): Contract | null {
    return this._byId().get(contractUID) ?? null;
  }

  /** All contracts for a given property — synchronous snapshot. */
  getForProperty(propertyUID: string): readonly Contract[] {
    return this._list().filter(c => c.propertyUID === propertyUID);
  }

  /** All contracts for a given tenant (matches tenantIds[] or legacy tenantUID). */
  getForTenant(tenantUID: string): readonly Contract[] {
    return this._list().filter(c =>
      (c.tenantIds && c.tenantIds.includes(tenantUID)) || c.tenantUID === tenantUID
    );
  }

  setList(contracts: readonly Contract[]): void {
    this._list.set(contracts);
  }

  upsert(contract: Contract): void {
    const list = this._list();
    const i = list.findIndex(c => c.contractUID === contract.contractUID);
    if (i === -1) {
      this._list.set([...list, contract]);
    } else {
      const next = list.slice();
      next[i] = contract;
      this._list.set(next);
    }
  }

  remove(contractId: string): void {
    this._list.set(this._list().filter(c => c.contractUID !== contractId));
    if (this._currentId() === contractId) {
      this._currentId.set(null);
    }
  }

  setCurrentId(id: string | null): void {
    this._currentId.set(id);
  }

  setLoading(value: boolean): void {
    this._loading.set(value);
  }

  reset(): void {
    this._list.set([]);
    this._currentId.set(null);
    this._loading.set(false);
  }

  /** Create a contract, optionally with payment-plan generation options. */
  addContract(
    contract: Contract,
    paymentGenerationOptions?: {
      strategy: 'all' | 'future' | 'custom';
      customStartDate?: Date;
      markPastAsPaid?: boolean;
    }
  ): Observable<string> {
    return this.service.addContract(contract, paymentGenerationOptions);
  }

  /** Update an existing contract. */
  updateContract(contract: Contract): Observable<void> {
    return this.service.updateContract(contract);
  }

  /** Delete a contract. */
  deleteContract(contractUID: string): Observable<void> {
    return this.service.deleteContract(contractUID);
  }

  /** Terminate a contract with the given termination record. */
  terminateContract(contractUID: string, termination: ContractTermination): Observable<void> {
    return this.service.terminateContract(contractUID, termination);
  }

  /** Append payments to a contract. */
  addPaymentsToContract(payments: Payment[], contractUID: string): Observable<void> {
    return this.service.addPaymentsToContract(payments, contractUID);
  }

  /** Replace a contract's planned-payments list. */
  updatePlannedPayments(contractUID: string, plannedPayments: PlannedPayment[]): Observable<void> {
    return this.service.updatePlannedPayments(contractUID, plannedPayments);
  }

  /** Observe a contract's payment history. */
  getPaymentHistory(contractUID: string): Observable<Payment[]> {
    return this.service.getPaymentHistory(contractUID);
  }

  /** Remove a payment from a contract. */
  removePayment(contractUID: string, paymentUID: string): Observable<void> {
    return this.service.removePayment(contractUID, paymentUID);
  }

  /** Release all or part of a contract's deposit. */
  releaseDeposit(contractUID: string, releaseAmount: number, releaseReason: string): Observable<void> {
    return this.service.releaseDeposit(contractUID, releaseAmount, releaseReason);
  }

  /** Generate future planned payments locally; returns an updated Contract. */
  generateFuturePlannedPayments(contract: Contract, targetYear?: number): Contract {
    return this.service.generateFuturePlannedPayments(contract, targetYear);
  }
}
